/**
 * Employment history input (input_subtype: employment_history) — Alpine
 * component. Moved from the inline script in
 * components/employment-history-input.blade.php: inline blade scripts die when
 * the component first appears via a Livewire morph (morphdom doesn't execute
 * injected script tags), which left the whole field dead — no autocomplete AND
 * no way to save manual input.
 *
 * Manual entry binds unconditionally (init + every moment the blade used to
 * re-init autocomplete); Google Places is a progressive enhancement layered on
 * top when the central loader resolves. Every keystroke in the address search
 * persists the raw string into currentEntry.address so a typed address
 * survives Save/Continue even if the user never presses Enter or selects a
 * suggestion — with or without Google.
 *
 * i18n: the inline blade script had `{{ __('formFilling.*') }}` baked in at
 * render time. In the bundle those strings come from the hidden `x-ref="i18n"`
 * JSON island the blade renders (a div, NOT a <script> — Livewire v2 eval()s
 * script innerHTML on morph); the literals below are English fallbacks.
 */
import loadGoogleMaps from '../lib/google-maps-loader';
import alvvaParsePlace from '../lib/google-place-parser';
import { unitField } from './unit-field';

export default function employmentHistoryInput(modelValue, yearsRequired) {
    return {
        entries: [],
        showForm: false,
        showParsedAddress: false,
        editingIndex: null,
        formError: '',
        modelValue: modelValue,
        desktopAutocomplete: null,
        parsedAddress: {
            street: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            country: ''
        },
        yearsRequired: yearsRequired,
        totalYears: 0,
        meetsRequirement: false,
        gaps: [],

        currentEntry: {
            type: 'employed',
            employer: '',
            school: '',
            job_title: '',
            address: '',
            from_date: '',
            to_date: '',
            is_current: false
        },

        // Server-rendered translations (English fallbacks; overridden from the
        // blade's hidden JSON island in loadI18n()).
        i18n: {
            type_employed: 'Employed',
            type_self_employed: 'Self-Employed',
            type_unemployed: 'Unemployed',
            type_student: 'Student',
            type_retired: 'Retired',
            type_homemaker: 'Homemaker',
            from_date_required: 'From date is required',
            to_date_required: 'To date is required (or mark as current)',
            date_order_error: 'To date must be after From date',
            employer_required: 'Employer name is required',
            school_required: 'School name is required',
            job_title_required: 'Job title is required',
            employer_address_required: 'Employer address is required',
            currently_work_here: 'I currently work here',
            currently_enrolled: 'I am currently enrolled',
            still_current: 'This is still current',
            unemployed_period: 'Unemployed',
            retired_period: 'Retired',
            homemaker_period: 'Homemaker',
            present: 'Present'
        },

        employmentTypes: [],

        // Apt/Ste/Flr asked as a choice plus a number, stored back into the
        // same key as one canonical string -- the payload shape is unchanged.
        ...unitField('parsedAddress.address2', 'updateAddressFromParsed'),

        init() {
            this.loadI18n();
            this.employmentTypes = [
                { value: 'employed', label: this.i18n.type_employed },
                { value: 'self_employed', label: this.i18n.type_self_employed },
                { value: 'unemployed', label: this.i18n.type_unemployed },
                { value: 'student', label: this.i18n.type_student },
                { value: 'retired', label: this.i18n.type_retired },
                { value: 'homemaker', label: this.i18n.type_homemaker }
            ];

            this.bindManualEntry();
            loadGoogleMaps()
                .then(() => this.initAutocomplete())
                .catch(() => {}); // silent fallback — manual entry already works

            this.parseExistingValue();
            this.watchUnit();
            this.$watch('modelValue', (value) => {
                if (value !== this.formatValue()) {
                    this.parseExistingValue();
                }
            });
            // Handle body overflow on mobile when sheet is open
            this.$watch('showForm', (value) => {
                if (window.innerWidth < 768) {
                    document.body.style.overflow = value ? 'hidden' : '';
                }
                if (value) {
                    this.$nextTick(() => {
                        this.bindManualEntry();
                        loadGoogleMaps()
                            .then(() => this.initAutocomplete())
                            .catch(() => {});
                    });
                }
            });
        },

        loadI18n() {
            try {
                const el = this.$refs.i18n;
                if (el) Object.assign(this.i18n, JSON.parse(el.textContent));
            } catch (e) {
                // Keep English fallbacks
            }
        },

        /** Always-on manual path: never depends on Google. */
        bindManualEntry() {
            const input = this.$refs.desktopAddressSearch;
            if (!input || input.dataset.manualBound === '1') return;
            input.dataset.manualBound = '1';

            // Persist raw text on every keystroke so nothing is lost if the
            // user taps Save/Continue without Enter/blur/selection.
            input.addEventListener('input', () => {
                if (!this.showParsedAddress) {
                    this.parsedAddress.street = input.value.trim();
                    this.updateAddressFromParsed();
                }
            });

            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = input.value.trim();
                    if (value) {
                        this.parsedAddress.street = value;
                        this.showParsedAddress = true;
                        this.updateAddressFromParsed();
                    }
                }
            });

            // Delay lets a click on a Places suggestion win: place_changed sets
            // showParsedAddress before this timeout inspects it.
            input.addEventListener('blur', () => {
                setTimeout(() => {
                    if (!this.showParsedAddress && input.value.trim()) {
                        this.parsedAddress.street = input.value.trim();
                        this.showParsedAddress = true;
                        this.updateAddressFromParsed();
                    }
                }, 250);
            });
        },

        initAutocomplete() {
            if (typeof google === 'undefined' || typeof google.maps === 'undefined') return;

            const input = this.$refs.desktopAddressSearch;
            // dataset marker (not this.desktopAutocomplete, which the reset
            // paths null out) guards against double-binding the same input.
            if (!input || input.dataset.pacBound === '1') return;
            input.dataset.pacBound = '1';

            // Local const: the place_changed closure must keep working even
            // after resetCurrentEntry()/editEntry() null this.desktopAutocomplete.
            const autocomplete = new google.maps.places.Autocomplete(input, {
                fields: ['address_components', 'formatted_address'],
                types: ['address']
            });
            this.desktopAutocomplete = autocomplete;

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place.address_components) {
                    this.parseAddressComponents(place);
                    this.showParsedAddress = true;
                    this.updateAddressFromParsed();
                    input.blur();
                }
            });
        },

        parseAddressComponents(place) {

            this.parsedAddress = { street: '', address2: '', city: '', state: '', zip: '', country: '' };

            // Shared parser: country-aware city/street fallbacks (UK
            // postal_town, sublocality wards, …) — see google-place-parser.
            const p = alvvaParsePlace(place);
            // address2 (apt/suite/floor) isn't in Google's "address" type
            // results — left blank for the user to fill in manually.
            this.parsedAddress = { street: p.street, address2: '', city: p.city, state: p.state, zip: p.zip, country: p.country };
        },

        updateAddressFromParsed() {
            const parts = [];
            if (this.parsedAddress.street) parts.push(this.parsedAddress.street);
            if (this.parsedAddress.address2) parts.push(this.parsedAddress.address2);
            if (this.parsedAddress.city) parts.push(this.parsedAddress.city);
            if (this.parsedAddress.state) parts.push(this.parsedAddress.state);
            if (this.parsedAddress.zip) parts.push(this.parsedAddress.zip);
            if (this.parsedAddress.country) parts.push(this.parsedAddress.country);
            this.currentEntry.address = parts.join(', ');
        },

        resetAddressSearch() {
            this.parsedAddress = { street: '', address2: '', city: '', state: '', zip: '', country: '' };
            this.currentEntry.address = '';
            this.showParsedAddress = false;
            this.desktopAutocomplete = null;

            this.$nextTick(() => {
                if (this.$refs.desktopAddressSearch) {
                    this.$refs.desktopAddressSearch.value = '';
                    this.$refs.desktopAddressSearch.focus();
                }
                this.bindManualEntry();
                loadGoogleMaps()
                    .then(() => this.initAutocomplete())
                    .catch(() => {});
            });
        },

        parseExistingAddress(addressStr) {
            // Best-effort parse for comma-separated addresses. Part count tells
            // us which shape we're looking at — updateAddressFromParsed() only
            // ever emits 5 parts (no address2) or 6 parts (address2 filled), so
            // we key off that instead of guessing positions, which used to
            // mis-map address2 into city for legacy 5-part values.
            const parts = addressStr.split(',').map(p => p.trim());

            if (parts.length === 6) {
                // street, address2, city, state, zip, country
                this.parsedAddress.street = parts[0] || '';
                this.parsedAddress.address2 = parts[1] || '';
                this.parsedAddress.city = parts[2] || '';
                this.parsedAddress.state = parts[3] || '';
                this.parsedAddress.zip = parts[4] || '';
                this.parsedAddress.country = parts[5] || '';
            } else if (parts.length === 5) {
                // Legacy format saved before address2 existed: street, city,
                // state, zip, country
                this.parsedAddress.street = parts[0] || '';
                this.parsedAddress.address2 = '';
                this.parsedAddress.city = parts[1] || '';
                this.parsedAddress.state = parts[2] || '';
                this.parsedAddress.zip = parts[3] || '';
                this.parsedAddress.country = parts[4] || '';
            } else if (parts.length === 4) {
                // street, city, state, country — Google resolves no postal
                // code for many non-US addresses, so this shape is produced
                // TODAY, not just by legacy data.
                this.parsedAddress.street = parts[0] || '';
                this.parsedAddress.address2 = '';
                this.parsedAddress.city = parts[1] || '';
                this.parsedAddress.state = parts[2] || '';
                this.parsedAddress.zip = '';
                this.parsedAddress.country = parts[3] || '';
            } else if (parts.length === 3) {
                // city, state, country — Google resolved only a locality
                // (no street precision).
                this.parsedAddress.street = '';
                this.parsedAddress.address2 = '';
                this.parsedAddress.city = parts[0] || '';
                this.parsedAddress.state = parts[1] || '';
                this.parsedAddress.zip = '';
                this.parsedAddress.country = parts[2] || '';
            } else {
                // Unrecognized shape — don't guess field boundaries (that's how
                // address2 used to land in city), just show it as street.
                this.parsedAddress.street = addressStr;
                this.parsedAddress.address2 = '';
                this.parsedAddress.city = '';
                this.parsedAddress.state = '';
                this.parsedAddress.zip = '';
                this.parsedAddress.country = '';
            }
        },

        parseExistingValue() {
            // Don't overwrite a field the user is actively editing — the
            // modelValue watcher fires on our own keystrokes too, which clobbered
            // fast typing (chars dropped / value flashed).
            if (this.$el && this.$el.contains(document.activeElement)) return;
            const value = this.modelValue || '';
            if (!value) {
                this.entries = [];
                this.calculateTotals();
                return;
            }

            try {
                const data = JSON.parse(value);
                if (data.entries && Array.isArray(data.entries)) {
                    this.entries = data.entries;
                    this.calculateTotals();
                }
            } catch (e) {
                this.entries = [];
                this.calculateTotals();
            }
        },

        resetCurrentEntry() {
            this.currentEntry = {
                type: 'employed',
                employer: '',
                school: '',
                job_title: '',
                address: '',
                from_date: '',
                to_date: '',
                is_current: false
            };
            this.parsedAddress = { street: '', address2: '', city: '', state: '', zip: '', country: '' };
            this.showParsedAddress = false;
            this.desktopAutocomplete = null;
            this.editingIndex = null;
            this.formError = '';
        },

        cancelForm() {
            this.showForm = false;
            this.resetCurrentEntry();
        },

        validateEntry() {
            if (!this.currentEntry.from_date) {
                this.formError = this.i18n.from_date_required;
                return false;
            }

            if (!this.currentEntry.is_current && !this.currentEntry.to_date) {
                this.formError = this.i18n.to_date_required;
                return false;
            }

            if (this.currentEntry.to_date && this.currentEntry.from_date > this.currentEntry.to_date) {
                this.formError = this.i18n.date_order_error;
                return false;
            }

            if (['employed', 'self_employed'].includes(this.currentEntry.type) && !this.currentEntry.employer.trim()) {
                this.formError = this.i18n.employer_required;
                return false;
            }

            if (this.currentEntry.type === 'student' && !this.currentEntry.school.trim()) {
                this.formError = this.i18n.school_required;
                return false;
            }

            if (this.isJob(this.currentEntry) && !(this.currentEntry.job_title || '').trim()) {
                this.formError = this.i18n.job_title_required;
                return false;
            }

            if (this.isJob(this.currentEntry) && !(this.currentEntry.address || '').trim()) {
                this.formError = this.i18n.employer_address_required;
                return false;
            }

            this.formError = '';
            return true;
        },

        isJob(entry) {
            return ['employed', 'self_employed'].includes(entry.type);
        },

        // Mirrors App\Rules\EmploymentEntriesComplete: entries saved before
        // title/address were required get flagged so the client can fix them.
        isEntryIncomplete(entry) {
            return this.isJob(entry)
                && (!(entry.job_title || '').trim() || !(entry.address || '').trim());
        },

        saveEntry() {
            if (!this.validateEntry()) return;

            const entry = { ...this.currentEntry };

            if (this.editingIndex !== null) {
                this.entries[this.editingIndex] = entry;
            } else {
                this.entries.push(entry);
            }

            // Sort entries by from_date descending (most recent first)
            this.entries.sort((a, b) => {
                return b.from_date.localeCompare(a.from_date);
            });

            this.calculateTotals();
            this.updateValue();
            this.cancelForm();
        },

        editEntry(index) {
            this.currentEntry = { ...this.entries[index] };
            this.editingIndex = index;
            this.formError = '';

            // Parse existing address into fields
            if (this.currentEntry.address) {
                this.parseExistingAddress(this.currentEntry.address);
                this.showParsedAddress = true;
            } else {
                this.parsedAddress = { street: '', address2: '', city: '', state: '', zip: '', country: '' };
                this.showParsedAddress = false;
            }

            this.desktopAutocomplete = null;
            this.showForm = true;
        },

        removeEntry(index) {
            this.entries.splice(index, 1);
            this.calculateTotals();
            this.updateValue();
        },

        // Overlapping periods are allowed (two jobs at once is normal) —
        // merge entries into a union of intervals so concurrent jobs
        // count calendar time once instead of double-counting toward the
        // years requirement, and gap detection sees the real timeline.
        mergedIntervals() {
            const now = new Date();
            const ranges = this.entries
                .map(e => ({
                    from: new Date(e.from_date),
                    to: e.is_current ? now : new Date(e.to_date),
                }))
                .filter(r => !isNaN(r.from) && !isNaN(r.to) && r.to >= r.from)
                .sort((a, b) => a.from - b.from);

            const merged = [];
            for (const r of ranges) {
                const last = merged[merged.length - 1];
                if (last && r.from <= last.to) {
                    if (r.to > last.to) last.to = r.to;
                } else {
                    merged.push({ from: r.from, to: r.to });
                }
            }
            return merged;
        },

        calculateTotals() {
            let totalMonths = 0;

            this.mergedIntervals().forEach(({ from, to }) => {
                const months = (to.getFullYear() - from.getFullYear()) * 12
                             + (to.getMonth() - from.getMonth());
                totalMonths += Math.max(0, months);
            });

            this.totalYears = totalMonths / 12;
            this.meetsRequirement = this.totalYears >= this.yearsRequired;

            this.detectGaps();
        },

        detectGaps() {
            this.gaps = [];
            const merged = this.mergedIntervals();
            if (merged.length < 2) return;

            for (let i = 0; i < merged.length - 1; i++) {
                const currentEnd = merged[i].to;
                const nextStart = merged[i + 1].from;

                // Check if there's a gap of more than 30 days
                const gapDays = (nextStart - currentEnd) / (1000 * 60 * 60 * 24);

                if (gapDays > 30) {
                    const gapMonths = Math.round(gapDays / 30);
                    this.gaps.push({
                        from: this.formatDisplayDate(currentEnd),
                        to: this.formatDisplayDate(nextStart),
                        months: gapMonths
                    });
                }
            }
        },

        formatDisplayDate(date) {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        },

        formatDateRange(entry) {
            const from = this.formatDisplayDate(new Date(entry.from_date));
            const to = entry.is_current ? this.i18n.present : this.formatDisplayDate(new Date(entry.to_date));
            return from + ' - ' + to;
        },

        formatDuration(entry) {
            const fromDate = new Date(entry.from_date);
            const toDate = entry.is_current ? new Date() : new Date(entry.to_date);
            const totalDays = (toDate - fromDate) / (1000 * 60 * 60 * 24);
            const months = Math.round(totalDays / 30.44);
            const years = Math.floor(months / 12);
            const remainingMonths = months % 12;

            let parts = [];
            if (years > 0) parts.push(years + (years === 1 ? ' yr' : ' yrs'));
            if (remainingMonths > 0) parts.push(remainingMonths + ' mo');
            return parts.join(' ') || '< 1 mo';
        },

        getTypeLabel(type) {
            const found = this.employmentTypes.find(t => t.value === type);
            return found ? found.label : type;
        },

        getToggleLabel() {
            switch (this.currentEntry.type) {
                case 'employed':
                case 'self_employed':
                    return this.i18n.currently_work_here;
                case 'student':
                    return this.i18n.currently_enrolled;
                default:
                    return this.i18n.still_current;
            }
        },

        getEntryTitle(entry) {
            switch (entry.type) {
                case 'employed':
                case 'self_employed':
                    return entry.employer;
                case 'student':
                    return entry.school;
                case 'unemployed':
                    return this.i18n.unemployed_period;
                case 'retired':
                    return this.i18n.retired_period;
                case 'homemaker':
                    return this.i18n.homemaker_period;
                default:
                    return entry.type;
            }
        },

        formatValue() {
            if (this.entries.length === 0) return '';

            return JSON.stringify({
                entries: this.entries,
                total_months: Math.round(this.totalYears * 12),
                total_years: parseFloat(this.totalYears.toFixed(2)),
                years_required: this.yearsRequired,
                meets_requirement: this.meetsRequirement,
                has_gaps: this.gaps.length > 0
            });
        },

        updateValue() {
            const formatted = this.formatValue();
            this.modelValue = formatted;

            if (this.$refs.hiddenInput) {
                this.$refs.hiddenInput.value = formatted;
                this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    }
}
